import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Play } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import HelpDot from '@/components/ui/HelpDot';
import Spinner from '@/components/ui/Spinner';
import WhyBox from '@/components/ui/WhyBox';
import type { Project } from '@/hooks/useProject';
import { apiFetch } from '@/lib/api';
import { getJson, useQuery } from '@/lib/query';

const FIELD_CLS =
  'mt-1 w-full rounded-md border border-border bg-elevated px-2.5 py-2 text-[13px] outline-none focus:border-primary';

/** A published Workflow Runner video workflow the producer can pick (engine-reported). */
interface VideoWorkflow {
  name: string;
  label: string;
  image_slots: number;
  /** Animates ONE opening frame (e.g. LTX) — needs photoreal storyboard frames, not sheets. */
  opening_frame_only: boolean;
  min_take_s: number;
  max_take_s: number;
  /** Its provider refuses realistic people (e.g. Seedance) — people are sketched, faces hidden. */
  hides_faces?: boolean;
}

interface VideoWorkflows {
  default: string;
  fallback: string;
  /** Used instead of a face-hiding default when the cast is photoreal people (Omni). */
  people?: string;
  workflows: VideoWorkflow[];
}

function Field({ label, help, children }: { label: string; help?: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="flex items-center gap-1.5 text-[12.5px] font-medium">
        {label}
        {help && <HelpDot>{help}</HelpDot>}
      </span>
      {children}
    </label>
  );
}

/**
 * The choices that belong to ONE production run — genre, style, voice, language.
 *
 * These live in a dialog rather than a permanent panel because they are per-run
 * parameters, not project settings. Anything that persists on the project (aspect
 * ratio, the Iconik collection) is in Settings instead, and shown read-only here so
 * you can still see what you're about to get.
 */
export default function NewProductionDialog({
  project,
  genres,
  styles,
  open,
  onOpenChange,
}: {
  project: Project;
  genres: { genre_key: string; display_name?: string; retired?: boolean }[];
  styles: { style_id: string; name?: string }[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const navigate = useNavigate();
  const { t } = useTranslation(['dashboard', 'common']);
  const isFootage = (project.production_type || 'generative') === 'footage';

  const [producing, setProducing] = useState(false);
  const [genreOverride, setGenreOverride] = useState('');
  const [styleId, setStyleId] = useState('');
  const [voiceMode, setVoiceMode] = useState('omni');
  const [voiceLanguage, setVoiceLanguage] = useState('');
  const [useCuttingRoom, setUseCuttingRoom] = useState(true);
  // Storyboard: 'sheet' (8-panel sheet per shot, default) or a first frame per shot
  // with the 'minimal' or 'full' prompt.
  const [storyboard, setStoryboard] = useState('sheet');
  // Video model: '' = the Admin panel's default video workflow.
  const [videoWorkflow, setVideoWorkflow] = useState('');
  const { data: videoOptions } = useQuery<VideoWorkflows>(
    open && !isFootage ? 'generation/video-workflows' : null,
    () => getJson<VideoWorkflows>(() => apiFetch('/api/generation/video-workflows')),
    { staleMs: 60_000 },
  );
  const workflows = videoOptions?.workflows ?? [];
  const defaultWorkflow = workflows.find((w) => w.name === videoOptions?.default);
  const pickedWorkflow = workflows.find((w) => w.name === videoWorkflow) ?? (videoWorkflow ? undefined : defaultWorkflow);
  // A model that only animates an opening frame would open on the pencil sheet.
  const needsFrames = !!pickedWorkflow?.opening_frame_only;
  const storyboardChoice = needsFrames && storyboard === 'sheet' ? 'minimal' : storyboard;
  const takeRange = (w: VideoWorkflow) => t('launch.videoTake', { min: w.min_take_s, max: w.max_take_s });
  const peopleWorkflow = workflows.find((w) => w.name === videoOptions?.people);
  // Picking a model picks its storyboard: sketched sheets for a model that hides faces,
  // photoreal first frames for one that accepts real people (the engine enforces this
  // for photoreal casts either way).
  const pickVideoWorkflow = (name: string) => {
    setVideoWorkflow(name);
    const w = workflows.find((x) => x.name === (name || videoOptions?.default));
    if (w) setStoryboard(w.hides_faces && !w.opening_frame_only ? 'sheet' : 'minimal');
  };

  const produce = async () => {
    setProducing(true);
    try {
      const resp = await apiFetch(`/api/projects/${project.id}/produce`, {
        method: 'POST',
        body: JSON.stringify({
          // Always attended — a human approves each phase (gates + live chat).
          attended: true,
          video_model: 'omni',
          aspect_ratio: project.aspect_ratio || undefined,
          use_cuttingroom: useCuttingRoom,
          voice_mode: voiceMode,
          voice_language: voiceLanguage.trim(),
          genre_override: genreOverride,
          style_id: styleId,
          // 'minimal' (default) = scene text + references; 'full' = the detailed prompt.
          storyboard_format: storyboardChoice === 'sheet' ? 'sheet' : 'frame',
          keyframe_prompt: storyboardChoice === 'sheet' ? 'minimal' : storyboardChoice,
          // '' = the Admin panel's default video workflow.
          video_workflow: videoWorkflow,
          // Story Engine always routes generation through Workflow Runner.
          generation_backend: 'workflow_runner',
        }),
      });
      if (resp.ok) {
        const data = await resp.json();
        navigate(`/projects/${project.id}/productions/${data.production_id}`);
      }
    } catch {
      /* surfaced by the disabled state */
    } finally {
      setProducing(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('launch.dialogTitle')}</DialogTitle>
          <DialogDescription>{t('launch.dialogSubtitle')}</DialogDescription>
        </DialogHeader>

        {isFootage && <WhyBox title={t('launch.footageTitle')}>{t('launch.footageBody')}</WhyBox>}

        <div className="mt-4 space-y-4">
          <Field label={t('launch.genre')} help={t('launch.genreHelp')}>
            <select className={FIELD_CLS} value={genreOverride} onChange={(e) => setGenreOverride(e.target.value)}>
              <option value="">{t('launch.genreAuto')}</option>
              {/* retired = no longer in Writers Room, the system of record */}
              {genres
                .filter((g) => !g.retired)
                .map((g) => (
                  <option key={g.genre_key} value={g.genre_key}>
                    {g.display_name || g.genre_key}
                  </option>
                ))}
            </select>
          </Field>

          {/* Style, voice and language only govern GENERATED picture and voices —
              meaningless when the footage is real. Genre stays: it steers the score
              and the paper cut's storytelling tone. */}
          {!isFootage && (
            <>
              <Field label={t('launch.style')} help={t('launch.styleHelp')}>
                <select className={FIELD_CLS} value={styleId} onChange={(e) => setStyleId(e.target.value)}>
                  <option value="">{t('launch.styleAuto')}</option>
                  {styles.map((s) => (
                    <option key={s.style_id} value={s.style_id}>
                      {s.name || s.style_id}
                    </option>
                  ))}
                </select>
              </Field>

              <Field label={t('launch.voice')} help={t('launch.voiceHelp')}>
                <select className={FIELD_CLS} value={voiceMode} onChange={(e) => setVoiceMode(e.target.value)}>
                  <option value="omni">{t('launch.voiceOmni')}</option>
                  <option value="runway">{t('launch.voiceRunway')}</option>
                </select>
              </Field>

              <Field label={t('launch.language')} help={t('launch.languageHelp')}>
                <input
                  className={FIELD_CLS}
                  value={voiceLanguage}
                  onChange={(e) => setVoiceLanguage(e.target.value)}
                  placeholder={t('launch.languagePlaceholder')}
                />
              </Field>

              {workflows.length > 0 && (
                <Field label={t('launch.videoModel')} help={t('launch.videoModelHelp')}>
                  <select className={FIELD_CLS} value={videoWorkflow} onChange={(e) => pickVideoWorkflow(e.target.value)}>
                    <option value="">
                      {defaultWorkflow
                        ? defaultWorkflow.hides_faces && peopleWorkflow && !peopleWorkflow.hides_faces
                          ? t('launch.videoModelDefaultPeople', {
                              label: defaultWorkflow.label,
                              people: peopleWorkflow.label,
                            })
                          : t('launch.videoModelDefaultNamed', { label: defaultWorkflow.label })
                        : t('launch.videoModelDefault')}
                    </option>
                    {workflows
                      .filter((w) => w.name !== videoOptions?.default)
                      .map((w) => (
                        <option key={w.name} value={w.name}>
                          {w.label} — {takeRange(w)}
                          {w.opening_frame_only ? ` · ${t('launch.videoOpeningFrame')}` : ''}
                          {w.hides_faces ? ` · ${t('launch.videoHidesFaces')}` : ` · ${t('launch.videoRealPeople')}`}
                        </option>
                      ))}
                  </select>
                </Field>
              )}

              <Field label={t('launch.keyframePrompt')} help={t('launch.keyframePromptHelp')}>
                <select className={FIELD_CLS} value={storyboardChoice} onChange={(e) => setStoryboard(e.target.value)}>
                  {!needsFrames && <option value="sheet">{t('launch.storyboardSheet')}</option>}
                  <option value="minimal">{t('launch.keyframePromptMinimal')}</option>
                  <option value="full">{t('launch.keyframePromptFull')}</option>
                </select>
                {needsFrames && (
                  <span className="mt-1 block text-[11.5px] text-muted-foreground">
                    {t('launch.videoNeedsFrames', { label: pickedWorkflow?.label ?? '' })}
                  </span>
                )}
              </Field>
            </>
          )}

          <label className="flex cursor-pointer items-center gap-2 text-[12.5px] text-muted-foreground">
            <input type="checkbox" checked={useCuttingRoom} onChange={(e) => setUseCuttingRoom(e.target.checked)} />
            {t('launch.cuttingRoom')}
            <HelpDot>{t('launch.cuttingRoomHelp')}</HelpDot>
          </label>

          <p className="m-0 border-t border-dashed border-border pt-3 text-[11.5px] text-faint">
            {t('launch.inheritedAspect', { aspect: project.aspect_ratio || '16:9' })}
          </p>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {t('action.cancel', { ns: 'common' })}
          </Button>
          <Button onClick={produce} disabled={producing}>
            {producing ? <Spinner className="mr-2" /> : <Play className="mr-2 h-4 w-4" />}
            {producing ? t('launch.starting') : t('launch.start')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
